/**
 * NexusGraphSection
 *
 * Self-contained graphs section for a Nexus surface (dashboard | contacts).
 * Loads the persisted per-workspace layout, renders the GraphGrid, and
 * debounce-saves layout changes back to the layout API
 * (FEATURE-01/02/03).
 *
 * Dependencies:
 * - GraphGrid
 * - /api/nexus/crm/graphs/layout
 * - lib/nexus/graph-metrics defaults + parsing
 */

'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { GraphGrid } from '@/components/nexus/graph-grid'
import {
  getDefaultContactsLayout,
  getDefaultDashboardLayout,
  parseNexusGraphLayout,
  type NexusGraphLayout,
  type NexusGraphPanelConfig
} from '@/lib/nexus/graph-metrics'
import { cn } from '@/lib/utils'

export interface NexusGraphSectionProps {
  workspaceId: string
  view: 'dashboard' | 'contacts'
  className?: string
  // Contacts page: filters from the active list to optionally sync.
  syncedFilters?: Partial<NexusGraphPanelConfig['filters']>
}

export function NexusGraphSection({
  workspaceId,
  view,
  className,
  syncedFilters
}: NexusGraphSectionProps) {
  const defaultLayout =
    view === 'contacts' ? getDefaultContactsLayout() : getDefaultDashboardLayout()

  const [layout, setLayout] = useState<NexusGraphLayout>(defaultLayout)
  const [loaded, setLoaded] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load persisted layout on mount / workspace change.
  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false

    fetch(
      `/api/nexus/crm/graphs/layout?workspaceId=${encodeURIComponent(workspaceId)}&view=${view}`,
      { cache: 'no-store' }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((payload: { layout?: unknown } | null) => {
        if (cancelled) return
        setLayout(parseNexusGraphLayout(payload?.layout, defaultLayout))
        setLoaded(true)
      })
      .catch(() => {
        if (!cancelled) setLoaded(true)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, view])

  const persist = useCallback(
    (next: NexusGraphLayout) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        void fetch('/api/nexus/crm/graphs/layout', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspaceId, view, layout: next })
        }).catch(() => undefined)
      }, 600)
    },
    [workspaceId, view]
  )

  const handleLayoutChange = useCallback(
    (next: NexusGraphLayout) => {
      setLayout(next)
      persist(next)
    },
    [persist]
  )

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  if (!workspaceId) return null

  return (
    <div className={cn('w-full', !loaded && 'opacity-90', className)}>
      <GraphGrid
        workspaceId={workspaceId}
        layout={layout}
        onLayoutChange={handleLayoutChange}
        syncedFilters={syncedFilters}
      />
    </div>
  )
}

export default NexusGraphSection
